import React, { Fragment, lazy, Suspense, useContext } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  CardActionArea,
  IconButton,
  Tooltip,
  Modal,
  Box,
} from "@mui/material";
import styled from "styled-components";
import { useQuery } from "react-query";
import { Link as RouterLink } from "react-router-dom";
import AnalyticsIcon from '@mui/icons-material/Analytics';
import { grey } from "@mui/material/colors";
import { useTheme } from '@mui/material/styles';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ImageIcon from '@mui/icons-material/Image';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import RocketIcon from '@mui/icons-material/Rocket';
import AddIcon from '@mui/icons-material/Add';
import { getStatsForDashboard } from "../../services/Dashboard";
import dualbg from '../../images/dual-bg.svg';
import { AnalyticsSkeleton } from "../common/DialogSkeleton";
import Check from "../../auth/ability";
import { CAN, SUBJECTS } from "../../auth/permissions";
import useModule from "../../hooks/useModule";
import { MODULES } from "../../services/Constants";
import { ACTIONS, WizardContext } from "../../components/wizard/Context";
import DottedTile from "../../components/common/DottedTile";
const ExtendedAnalytics = lazy(() => import('./ExtendedAnalytics'));

const DashboardStats = () => {
  const { validateModuleWithMessage } = useModule();
  const { dispatch } = useContext(WizardContext);
  const [type, setType] = React.useState("vehicle");
  const [open, setOpen] = React.useState(false);
  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 1200,
    bgcolor: 'background.paper',
    p: 4,
  };

  const theme = useTheme();
  const AnalyticsWrapper = styled.div`
    width: 100%;
    position: absolute;
    text-align: end;
    button {
        color: ${theme.custom.dualTheme ? theme.palette.primary[600] : grey[500]}
    }
    `;

  const { data } = useQuery(
    "getStatsForDashboard",
    getStatsForDashboard,
    {
      refetchOnWindowFocus: false,
      useErrorBoundary: false,
      suspense: true,
    }
  );

  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Grid container rowSpacing={2} columnSpacing={{ xs: 1, sm: 2, md: 2 }} columns={{ xs: 4, sm: 8, md: 12 }}>
      <Grid size={{ xs: 2, sm: 2, md: 2.33 }}>
        <CardActionArea LinkComponent={RouterLink} to={`/vehicles`}>
          <Card variant="outlined" sx={{ padding: "3%" }}>
            {theme.custom.dualTheme &&
              <AnalyticsWrapper>
                <Tooltip title="Show Analytics">
                  <IconButton aria-label="analytics" alt
                    onClick={(e) => {
                      e.preventDefault();
                      setType("vehicle");
                      handleOpen();
                    }}>
                    <AnalyticsIcon />
                  </IconButton>
                </Tooltip>
              </AnalyticsWrapper>}
            <CardContent
              {...(theme.custom.dualTheme && { sx: { backgroundImage: `url('${dualbg}')`, backgroundSize: 'cover' } })}
            >
              <div style={{ borderRadius: "8px", backgroundColor: "#f0f5ff", width: 60, height: 60, display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '10%' }}>
                <DirectionsCarIcon sx={{ color: '#3b82f6', fontSize: "26px" }} />
              </div>
              <Typography color="text.secondary" sx={{ fontSize: 16 }}>
                Vehicles Provisioned
              </Typography>
              <Typography
                color={theme.palette.primary['A700']}
                sx={{ fontSize: 44, fontWeight: 500 }}
              >
                {data?.vehicles ? data.vehicles : "-"}
              </Typography>
            </CardContent>
          </Card>
        </CardActionArea>
      </Grid>
      <Grid size={{ xs: 2, sm: 2, md: 2.33 }}>
        <CardActionArea LinkComponent={RouterLink} to={`/images`}>
          <Card variant="outlined" sx={{ padding: "3%" }}>
            {theme.custom.dualTheme &&
              <AnalyticsWrapper>
                <Tooltip title="Show Analytics">
                  <IconButton aria-label="analytics" alt
                    onClick={(e) => {
                      e.preventDefault();
                      setType("image");
                      handleOpen();
                    }}>
                    <AnalyticsIcon />
                  </IconButton>
                </Tooltip>
              </AnalyticsWrapper>}
            <CardContent
              {...(theme.custom.dualTheme && { sx: { backgroundImage: `url('${dualbg}')`, backgroundSize: 'cover' } })}
            >
              <div style={{ borderRadius: "8px", backgroundColor: "#ebf4ee", width: 60, height: 60, display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '10%' }}>
                <ImageIcon sx={{ color: '#006e26', fontSize: "26px" }} />
              </div>
              <Typography color="text.secondary">Images uploaded</Typography>
              <Typography
                color={theme.palette.primary['A700']}
                sx={{ fontSize: 44, fontWeight: 500 }}
              >
                {data?.images ? data.images : "-"}
              </Typography>
            </CardContent>
          </Card>
        </CardActionArea>
      </Grid>
      <Grid size={{ xs: 2, sm: 2, md: 2.33 }}>
        <CardActionArea LinkComponent={RouterLink} to={`/releases`}>
          <Card variant="outlined" sx={{ padding: "3%" }}>
            {theme.custom.dualTheme &&
              <AnalyticsWrapper>
                <Tooltip title="Show Analytics">
                  <IconButton aria-label="analytics" alt
                    onClick={(e) => {
                      e.preventDefault();
                      setType("release");
                      handleOpen();
                    }}>
                    <AnalyticsIcon />
                  </IconButton>
                </Tooltip>
              </AnalyticsWrapper>}
            <CardContent
              {...(theme.custom.dualTheme && { sx: { backgroundImage: `url('${dualbg}')`, backgroundSize: 'cover' } })}
            >
              <div style={{ borderRadius: "8px", backgroundColor: "#f7f1eb", width: 60, height: 60, display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '10%' }}>
                <NewReleasesIcon sx={{ color: '#964300', fontSize: "26px" }} />
              </div>
              <Typography color="text.secondary">Releases</Typography>
              <Typography
                color={theme.palette.primary['A700']}
                sx={{ fontSize: 44, fontWeight: 500 }}
              >
                {data?.releases ? data.releases : "-"}
              </Typography>
            </CardContent>
          </Card>
        </CardActionArea>
      </Grid>
      <Grid size={{ xs: 2, sm: 2, md: 2.33 }}>
        <CardActionArea LinkComponent={RouterLink} to={`/deployments`}>
          <Card variant="outlined" sx={{ padding: "3%" }}>
            {theme.custom.dualTheme &&
              <AnalyticsWrapper>
                <Tooltip title="Show Analytics">
                  <IconButton aria-label="analytics" alt
                    onClick={(e) => {
                      e.preventDefault();
                      setType("deployment");
                      handleOpen();
                    }}>
                    <AnalyticsIcon />
                  </IconButton>
                </Tooltip>
              </AnalyticsWrapper>}
            <CardContent
              {...(theme.custom.dualTheme && { sx: { backgroundImage: `url('${dualbg}')`, backgroundSize: 'cover' } })}
            >
              <div style={{ borderRadius: "8px", backgroundColor: "#f3f1f8", width: 60, height: 60, display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '10%' }}>
                <RocketIcon sx={{ color: "#6750a4", fontSize: "26px" }} />
              </div>
              <Typography color="text.secondary">Deployments</Typography>
              <Typography
                color={theme.palette.primary['A700']}
                sx={{ fontSize: 44, fontWeight: 500 }}
              >
                {data?.deployments ? data.deployments : "-"}
              </Typography>
            </CardContent>
          </Card>
        </CardActionArea>
      </Grid>
      <Check I={CAN.CREATE} a={SUBJECTS.DEPLOYMENT}>
        <Grid size={{ xs: 2, sm: 2, md: 2.33 }}>
          <CardActionArea
            onClick={() => {
              if (validateModuleWithMessage(MODULES.OTA)) {
                dispatch({
                  type: ACTIONS.TOGGLE_WIZARD,
                  payload: {
                    showWizard: true,
                  },
                })
              }
            }}
          >
            <DottedTile title={"NEW DEPLOYMENT"} padding={"15.5%"}>
              <AddIcon
                sx={{ fontSize: "5.4rem", color: theme.palette.primary[200] }}
              />
            </DottedTile>
          </CardActionArea>
        </Grid>
      </Check>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="parent-modal-title"
        aria-describedby="parent-modal-description"
        width="1200px"
      >
        <Box sx={style}>
          <div style={{
            minWidth: 0,
            height: 400,
            marginBottom: '5%'
          }}>
            <Suspense fallback={<AnalyticsSkeleton />}>
              <ExtendedAnalytics type={type} color={theme.palette.primary[600]} />
            </Suspense>
          </div>
        </Box>
      </Modal>
    </Grid>
  );
};

DashboardStats.defaultProps = {};

DashboardStats.propTypes = {};

export default DashboardStats;
